import { useEffect, useState } from "react";
import { getAuthHeaders } from "../api";

// <img> tags can't attach an Authorization header, and the
// /images/:caseId/:key endpoint requires a Clerk JWT via Bearer
// auth — so a bare `<img src="https://api.casethread.app/...">`
// silently 403s. This helper fetches the image as a blob via the
// same authenticated fetch path `request()` uses, wraps it in an
// object URL, and hands that to <img>. Object URLs are revoked on
// unmount (or when `src` changes) so we don't leak blob memory.

interface Props {
  src: string;
  alt?: string;
  className?: string;
  loading?: "eager" | "lazy";
  onClick?: () => void;
  title?: string;
}

export default function AuthImage({
  src,
  alt = "",
  className,
  loading = "lazy",
  onClick,
  title,
}: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setError(false);
    setObjectUrl(null);
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(src, {
          credentials: "include",
          headers,
        });
        if (!res.ok) throw new Error(`http_${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src]);

  if (error) {
    return (
      <div className={`${className ?? ""} auth-image-error`} title="Image unavailable">
        ⚠
      </div>
    );
  }
  if (!objectUrl) {
    return <div className={`${className ?? ""} auth-image-loading`} />;
  }
  return (
    <img
      src={objectUrl}
      alt={alt}
      className={className}
      loading={loading}
      onClick={onClick}
      title={title}
    />
  );
}
